/**
 * Prep Timeline Utility
 *
 * Calculates backward-scheduled cooking timelines so users know
 * exactly when to start each phase for a target mealtime.
 */
import { localeFor } from './dateLocale';

export interface TimelineBlock {
  labelKey: string; // the step's name, for t()
  time: string;    // HH:MM format
}

export type PrepTimeline =
  | { type: 'full'; blocks: [TimelineBlock, TimelineBlock, TimelineBlock] }
  | { type: 'partial'; blocks: [TimelineBlock, TimelineBlock] }
  | { type: 'unavailable' };

interface RecipeTimingInput {
  preparationMinutes?: number;
  cookingMinutes?: number;
  readyInMinutes?: number;
}

/**
 * Parse "HH:MM" string into total minutes since midnight.
 */
function parseTime(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Format total minutes since midnight into "HH:MM" (24h).
 */
function formatTime(totalMinutes: number): string {
  // Handle wrapping past midnight or before midnight
  const wrapped = ((totalMinutes % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

/**
 * A time of day as the language writes it: "5:30 PM", « 17 h 30 ».
 */
export function formatTimeDisplay(hhmm: string, language: string = 'en'): string {
  const [h, m] = hhmm.split(':').map(Number);
  return new Intl.DateTimeFormat(localeFor(language.slice(0, 2)), { hour: 'numeric', minute: '2-digit' })
    .format(new Date(2000, 0, 1, h, m))
    .replace(/\u202f/g, ' ');
}

/**
 * Calculate the prep timeline for a recipe given a target mealtime.
 *
 * - Full data (preparationMinutes + cookingMinutes): 3-block timeline
 * - Partial data (readyInMinutes only): 2-block timeline
 * - No data: unavailable
 */
export function calculatePrepTimeline(
  recipe: RecipeTimingInput,
  targetTime: string, // "HH:MM"
): PrepTimeline {
  const targetMinutes = parseTime(targetTime);

  const hasPrep = typeof recipe.preparationMinutes === 'number' && recipe.preparationMinutes > 0;
  const hasCook = typeof recipe.cookingMinutes === 'number' && recipe.cookingMinutes > 0;

  // Full data: both prep and cook times available
  if (hasPrep && hasCook) {
    const prepStart = targetMinutes - recipe.preparationMinutes! - recipe.cookingMinutes!;
    const cookStart = targetMinutes - recipe.cookingMinutes!;

    return {
      type: 'full',
      blocks: [
        { labelKey: 'cooking.timelineStartPrep', time: formatTime(prepStart) },
        { labelKey: 'cooking.timelineStartCooking', time: formatTime(cookStart) },
        { labelKey: 'cooking.timelineReady', time: formatTime(targetMinutes) },
      ],
    };
  }

  // Partial data: only readyInMinutes
  if (typeof recipe.readyInMinutes === 'number' && recipe.readyInMinutes > 0) {
    const startTime = targetMinutes - recipe.readyInMinutes;

    return {
      type: 'partial',
      blocks: [
        { labelKey: 'cooking.timelineStart', time: formatTime(startTime) },
        { labelKey: 'cooking.timelineReady', time: formatTime(targetMinutes) },
      ],
    };
  }

  return { type: 'unavailable' };
}
